import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ChevronRight, ChevronDown } from 'lucide-react';
import api from '../services/api';

// Muted Luxury Environments - Quiet Luxury / Architectural Brutalism
const ENVIRONMENTS = {
  corridor: 'https://static.prod-images.emergentagent.com/jobs/2c0beaa6-c4c8-45fb-8720-fb0ecbfcdc2f/images/91a5be1b3c5ef336f909fe391748239bb762b2bb9064dd2b9a2a611a4bc019a7.png',
  executiveLab: 'https://static.prod-images.emergentagent.com/jobs/2c0beaa6-c4c8-45fb-8720-fb0ecbfcdc2f/images/be80360c0b7964e1d07eae546100d89db9c15f7c6a7676649688b95ab33abf07.png',
  brutalistTunnel: 'https://static.prod-images.emergentagent.com/jobs/2c0beaa6-c4c8-45fb-8720-fb0ecbfcdc2f/images/72b025f3c0f4d2faded0c203dfbe9be9f2e10d68f3611c04bd6a5e84f6adde4f.png',
  technicalBackdrop: 'https://static.prod-images.emergentagent.com/jobs/2c0beaa6-c4c8-45fb-8720-fb0ecbfcdc2f/images/001305bc36fc297dea6e4bf53eb5516e8e0e6c1e653615833886b137d472590a.png'
};

// Technical callouts - understated style
const TECH_CALLOUTS = {
  suit: [
    { label: 'Comfort Stretch', position: { x: 25, y: 35 }, detail: '4-way stretch blend' },
    { label: 'Athletic Fit', position: { x: 70, y: 45 }, detail: 'Tailored silhouette' },
    { label: 'Breathable Lining', position: { x: 30, y: 65 }, detail: 'Moisture-wicking' }
  ]
};

const TunnelVisionExperience = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [products, setProducts] = useState({ suit: null, jacket: null, pants: null, duffle: null });
  const [loading, setLoading] = useState(true);
  const [visibleCallouts, setVisibleCallouts] = useState([]);

  // Fetch products from Shopify
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const [suitRes, jacketRes, pantsRes, duffleRes] = await Promise.all([
          api.get('/shopify/products/taylor-suit').catch(() => null),
          api.get('/shopify/products/the-taylor-jacket').catch(() => null),
          api.get('/shopify/products/the-taylor-pants-1').catch(() => null),
          api.get('/shopify/products/the-valley-duffle').catch(() => null)
        ]);
        
        setProducts({
          suit: suitRes?.data?.product || null,
          jacket: jacketRes?.data?.product || null,
          pants: pantsRes?.data?.product || null,
          duffle: duffleRes?.data?.product || null
        });
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Scroll tracking for parallax
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min(scrollTop / docHeight, 1);
      setScrollProgress(progress);
      
      // Reveal callouts progressively
      const calloutProgress = (progress - 0.25) / 0.4;
      if (calloutProgress > 0) {
        const numCallouts = Math.floor(calloutProgress * 3);
        setVisibleCallouts(Array.from({ length: numCallouts }, (_, i) => i));
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getPrice = (product) => {
    if (!product) return 0;
    return product.variants?.[0]?.price || product.price || 0;
  };

  const getProductImage = (product) => {
    if (!product?.images?.length) return '';
    return product.images[0]?.src || product.images[0]?.url || '';
  };

  const handleShopProduct = (handle) => {
    navigate(`/product/${handle}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-stone-600 border-t-stone-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-stone-500 uppercase tracking-[0.3em] text-xs">Entering</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-[#0a0a0a] text-stone-200 overflow-x-hidden">
      {/* Minimal Progress Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 h-px bg-stone-900">
        <div 
          className="h-full bg-stone-500 transition-all duration-100"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      {/* Section 1: Hero - The Tunnel Entrance */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Parallax Background - Brutalist Corridor */}
        <div 
          className="absolute inset-0 transition-transform duration-500"
          style={{ transform: `scale(${1 + scrollProgress * 0.08}) translateY(${scrollProgress * -30}px)` }}
        >
          <img 
            src={ENVIRONMENTS.corridor}
            alt="Tunnel"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-transparent to-[#0a0a0a]"></div>
        </div>

        {/* Hero Content - Understated */}
        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <p className="text-stone-500 uppercase tracking-[0.4em] text-xs mb-6">
            Tunnel Vision Collection
          </p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-light uppercase tracking-[0.1em] mb-8 text-stone-100">
            Enter The Tunnel
          </h1>
          <p className="text-lg text-stone-400 mb-12 font-light tracking-wide">
            Where focus becomes presence.
          </p>
          
          <ChevronDown className="w-5 h-5 text-stone-600 mx-auto animate-bounce" />
        </div>
      </section>

      {/* Section 2: The Taylor Suit - Hero Product */}
      <section className="relative min-h-[150vh] py-32">
        {/* Environment Background */}
        <div 
          className="absolute inset-0 transition-all duration-700"
          style={{ opacity: Math.max(0.3, 0.7 - scrollProgress) }}
        >
          <img 
            src={ENVIRONMENTS.executiveLab}
            alt="Executive Lab"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0a0a0a]/60"></div>
          
          {/* Spotlight Effect - Global Illumination Simulation */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at 50% 30%, rgba(120,120,110,0.12) 0%, transparent 50%)'
            }}
          />
        </div>

        {/* Product Content */}
        <div className="relative z-10 container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center min-h-screen">
            {/* Product Image with Scene Harmonization */}
            <div className="relative">
              <div 
                className="relative transition-all duration-700"
                style={{ transform: `translateY(${(scrollProgress - 0.15) * -20}px)` }}
              >
                {/* Soft Ambient Glow */}
                <div className="absolute -inset-20 bg-stone-500/5 blur-3xl rounded-full"></div>
                
                {/* Product Container with Harmonization Effects */}
                <div className="relative">
                  {/* Product Image */}
                  <img 
                    src={getProductImage(products.suit)}
                    alt="The Taylor Suit"
                    className="relative w-full max-w-md mx-auto rounded-sm"
                    style={{ 
                      filter: 'drop-shadow(0 40px 80px rgba(0,0,0,0.7)) contrast(1.05) brightness(0.95)',
                    }}
                  />
                  
                  {/* Light Catch Overlay - Simulates environment light on product */}
                  <div 
                    className="absolute inset-0 pointer-events-none rounded-sm"
                    style={{
                      background: 'linear-gradient(160deg, rgba(180,175,165,0.06) 0%, transparent 25%, transparent 75%, rgba(0,0,0,0.15) 100%)',
                      mixBlendMode: 'overlay'
                    }}
                  />
                </div>

                {/* Technical Callouts - Muted Style */}
                {TECH_CALLOUTS.suit.map((callout, index) => (
                  <div
                    key={callout.label}
                    className="absolute transition-all duration-700"
                    style={{
                      left: `${callout.position.x}%`,
                      top: `${callout.position.y}%`,
                      opacity: visibleCallouts.includes(index) ? 1 : 0,
                      transform: `translateX(-50%) translateY(${visibleCallouts.includes(index) ? 0 : 10}px)`
                    }}
                  >
                    <div className="relative">
                      <div className="w-2 h-2 bg-stone-500 rounded-full"></div>
                      <div className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap">
                        <div className="w-6 h-px bg-stone-600"></div>
                        <div className="absolute left-8 top-1/2 -translate-y-1/2 bg-[#0a0a0a]/90 backdrop-blur-sm border border-stone-800 px-3 py-2 rounded">
                          <p className="text-stone-400 text-[10px] font-medium uppercase tracking-wider">{callout.label}</p>
                          <p className="text-stone-600 text-[9px] mt-0.5">{callout.detail}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product Info - Quiet Luxury */}
            <div className="space-y-8">
              <div>
                <p className="text-stone-600 uppercase tracking-[0.3em] text-xs mb-3">The Complete Look</p>
                <h2 className="text-3xl md:text-5xl font-light uppercase tracking-wide text-stone-100">
                  The Taylor Suit
                </h2>
              </div>

              <p className="text-lg text-stone-400 leading-relaxed font-light">
                Pre-game focus meets post-game presence. Engineered for those who command 
                attention from the tunnel to the podium.
              </p>

              <div className="flex items-baseline gap-4">
                <span className="text-3xl font-light text-stone-200">${getPrice(products.suit)}</span>
                <span className="text-stone-700 line-through text-sm">$450</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {['Comfort Stretch', 'Athletic Fit', 'All-Day Wear'].map((tag) => (
                  <span key={tag} className="px-4 py-2 bg-stone-900/50 border border-stone-800 rounded text-xs text-stone-500 uppercase tracking-wider">
                    {tag}
                  </span>
                ))}
              </div>

              <Button 
                onClick={() => handleShopProduct('taylor-suit')}
                className="bg-stone-100 hover:bg-white text-stone-900 font-medium text-sm px-8 py-6 uppercase tracking-[0.2em] rounded-none"
                data-testid="shop-taylor-suit-btn"
              >
                Shop The Suit
                <ChevronRight className="w-4 h-4 ml-3" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Build Your Way - Separate Pieces */}
      <section className="relative min-h-screen py-32 bg-gradient-to-b from-[#0a0a0a] via-[#0d0d0d] to-[#0a0a0a]">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <p className="text-stone-600 uppercase tracking-[0.3em] text-xs mb-4">Or Build Your Way</p>
            <h2 className="text-3xl md:text-4xl font-light uppercase tracking-wide text-stone-200">
              Separate Pieces
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* The Taylor Jacket */}
            <div 
              className="group relative bg-[#0d0d0d] border border-stone-900 overflow-hidden hover:border-stone-700 transition-all duration-500"
              style={{
                transform: `translateY(${Math.max(0, (0.45 - scrollProgress) * 60)}px)`,
                opacity: Math.min(1, (scrollProgress - 0.3) * 3)
              }}
            >
              {/* Subtle Environment Background */}
              <div className="absolute inset-0 opacity-10">
                <img src={ENVIRONMENTS.technicalBackdrop} alt="" className="w-full h-full object-cover" />
              </div>
              
              <div className="relative p-10">
                <div className="aspect-[4/5] mb-8 flex items-center justify-center overflow-hidden">
                  <img 
                    src={getProductImage(products.jacket)}
                    alt="The Taylor Jacket"
                    className="max-h-72 object-contain group-hover:scale-105 transition-transform duration-700"
                    style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5)) brightness(0.95)' }}
                  />
                </div>
                
                <h3 className="text-xl font-light uppercase tracking-wide text-stone-200 mb-2">The Taylor Jacket</h3>
                <p className="text-stone-600 text-sm mb-6 font-light">Structured shoulders. Movement freedom.</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xl font-light text-stone-300">${getPrice(products.jacket)}</span>
                  <Button 
                    onClick={() => handleShopProduct('the-taylor-jacket')}
                    variant="outline"
                    className="border-stone-700 text-stone-400 hover:bg-stone-800 hover:text-stone-200 rounded-none text-xs uppercase tracking-wider"
                    data-testid="shop-taylor-jacket-btn"
                  >
                    Shop Jacket
                  </Button>
                </div>
              </div>
            </div>

            {/* The Taylor Pants */}
            <div 
              className="group relative bg-[#0d0d0d] border border-stone-900 overflow-hidden hover:border-stone-700 transition-all duration-500"
              style={{
                transform: `translateY(${Math.max(0, (0.5 - scrollProgress) * 60)}px)`,
                opacity: Math.min(1, (scrollProgress - 0.35) * 3)
              }}
            >
              <div className="absolute inset-0 opacity-10">
                <img src={ENVIRONMENTS.brutalistTunnel} alt="" className="w-full h-full object-cover" />
              </div>
              
              <div className="relative p-10">
                <div className="aspect-[4/5] mb-8 flex items-center justify-center overflow-hidden">
                  <img 
                    src={getProductImage(products.pants)}
                    alt="The Taylor Pants"
                    className="max-h-72 object-contain group-hover:scale-105 transition-transform duration-700"
                    style={{ filter: 'drop-shadow(0 20px 40px rgba(0,0,0,0.5)) brightness(0.95)' }}
                  />
                </div>
                
                <h3 className="text-xl font-light uppercase tracking-wide text-stone-200 mb-2">The Taylor Pants</h3>
                <p className="text-stone-600 text-sm mb-6 font-light">Flex waistband. Tapered leg.</p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xl font-light text-stone-300">${getPrice(products.pants)}</span>
                  <Button 
                    onClick={() => handleShopProduct('the-taylor-pants-1')}
                    variant="outline"
                    className="border-stone-700 text-stone-400 hover:bg-stone-800 hover:text-stone-200 rounded-none text-xs uppercase tracking-wider"
                    data-testid="shop-taylor-pants-btn"
                  >
                    Shop Pants
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: The Valley Duffle - Complete the Kit */}
      <section className="relative min-h-screen py-32 overflow-hidden">
        {/* Executive Lab Environment */}
        <div 
          className="absolute inset-0"
          style={{ opacity: Math.min(0.4, (scrollProgress - 0.5) * 1.2) }}
        >
          <img 
            src={ENVIRONMENTS.technicalBackdrop}
            alt="Technical Space"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent"></div>
        </div>

        <div className="relative z-10 container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[70vh]">
            {/* Duffle Info */}
            <div className="space-y-8 order-2 lg:order-1">
              <div>
                <p className="text-stone-600 uppercase tracking-[0.3em] text-xs mb-3">Complete The Kit</p>
                <h2 className="text-3xl md:text-5xl font-light uppercase tracking-wide text-stone-100">
                  The Valley Duffle
                </h2>
              </div>
              
              <p className="text-lg text-stone-400 leading-relaxed font-light">
                From the parking lot to the private jet. Premium hardware, reinforced construction.
              </p>

              <div className="text-2xl font-light text-stone-200">${getPrice(products.duffle)}</div>

              <Button 
                onClick={() => handleShopProduct('the-valley-duffle')}
                className="bg-stone-100 hover:bg-white text-stone-900 font-medium text-sm px-8 py-6 uppercase tracking-[0.2em] rounded-none"
                data-testid="shop-valley-duffle-btn"
              >
                Shop The Duffle
                <ChevronRight className="w-4 h-4 ml-3" />
              </Button>
            </div>

            {/* Duffle Image */}
            <div 
              className="order-1 lg:order-2"
              style={{
                transform: `translateX(${Math.max(0, (0.55 - scrollProgress) * 50)}px)`,
                opacity: Math.min(1, (scrollProgress - 0.5) * 3)
              }}
            >
              <img 
                src={getProductImage(products.duffle)}
                alt="The Valley Duffle"
                className="w-full max-w-sm mx-auto"
                style={{ filter: 'drop-shadow(0 30px 60px rgba(0,0,0,0.7)) brightness(0.95)' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Final CTA */}
      <section className="relative min-h-[60vh] flex items-center justify-center py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0d0d0d] to-[#0a0a0a]"></div>
        
        <div className="relative z-10 text-center px-6 max-w-2xl mx-auto">
          <p className="text-stone-600 uppercase tracking-[0.4em] text-xs mb-8">Tunnel Vision Collection</p>
          <h2 className="text-3xl md:text-4xl font-light uppercase tracking-wide mb-8 text-stone-200">
            Own The Moment
          </h2>
          <p className="text-stone-500 mb-12 font-light">
            From tunnel walk to postgame. The complete kit for those who refuse to blend in.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/collections')}
              className="bg-stone-100 hover:bg-white text-stone-900 font-medium text-sm px-10 py-6 uppercase tracking-[0.2em] rounded-none"
              data-testid="shop-full-collection-btn"
            >
              Shop Collection
              <ChevronRight className="w-4 h-4 ml-3" />
            </Button>
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="border-stone-800 text-stone-500 hover:bg-stone-900 font-medium text-sm px-10 py-6 uppercase tracking-[0.2em] rounded-none"
            >
              Back to Store
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TunnelVisionExperience;
